import { useState } from 'react';
import { T } from 'gt-react';
import { CaretDownIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import {
  ErpSyncErrorMetaSchema,
  type ErpSyncErrorMeta,
} from '@erp/shared-types';
import type { ProcessingError } from 'src/types/application/SalesOrderDraftDTO';

const ERP_SYNC_FAILED = 'ERP_SYNC_FAILED';

interface Props {
  /** Current draft status — the banner only shows while in the ERP_SYNC_FAILED state. */
  status?: string;
  /** The draft's processing errors; the ERP_SYNC_FAILED entry supplies the message. */
  processingErrors?: ProcessingError[];
}

/**
 * `meta` is JSONB the API hands over as `unknown`, so the report's shape is not
 * guaranteed at runtime — a row written by an older build, or one hand-edited in
 * the DB, could carry a `report` without `groups`. Rendering maps over
 * `groups`/`items`, so a malformed report would throw and take down the whole
 * draft modal — over an error banner. Anything that doesn't structurally check
 * out is dropped back to the plain-message path.
 */
function getErpSyncMeta(
  processingErrors?: ProcessingError[],
): ErpSyncErrorMeta | undefined {
  const entry = processingErrors?.find((e) => e.type === ERP_SYNC_FAILED);
  if (!entry) return undefined;

  const parsed = ErpSyncErrorMetaSchema.safeParse(entry.meta ?? {});
  if (parsed.success) return parsed.data;

  // Preserve the legacy text even when a malformed report makes the complete
  // metadata object invalid. An error banner must degrade, never crash.
  if (typeof entry.meta === 'object' && entry.meta !== null) {
    if ('message' in entry.meta && typeof entry.meta.message === 'string') {
      return { message: entry.meta.message };
    }
  }
  return {};
}

/**
 * Banner shown on a draft whose auto-approval failed to sync to the ERP
 * (Famous) — status `ERP_SYNC_FAILED`. The order was NOT approved and nothing
 * was created in Famous; the draft is safe to re-approve, which retries the
 * full create + sync. Gated on the current status so it disappears the moment
 * the order leaves the failed state (by any approval path), even if a stale
 * error entry lingers.
 *
 * Prefers the structured `meta.report` — the ERP's raw text names its own record
 * numbers and internal codes, which is unreadable and, worse, un-actionable
 * (SUPP-206: thirteen repetitions of "Invalid Customer Product Code: 43009"
 * where the real problem was one unassigned product list). The report states the
 * problem once, names the affected products, and gives the fix; the raw text
 * stays available behind "Show raw ERP response" for engineering.
 *
 * Falls back to `meta.message` alone for drafts that failed before this shipped.
 */
export function ErpSyncErrorBanner({ status, processingErrors }: Props) {
  const [showRaw, setShowRaw] = useState(false);

  if (status !== ERP_SYNC_FAILED) return null;
  const meta = getErpSyncMeta(processingErrors);
  if (meta === undefined) return null;

  const { message, report } = meta;

  return (
    <AlertContainer className="mb-3 border-red-200 bg-red-50">
      <WarningOctagonIcon className="h-4 w-4 shrink-0 text-red-600" />
      <AlertDescription className="ml-2 flex flex-col gap-2 text-sm text-red-900">
        <span className="font-medium">
          <T context="erp sync failed banner on sales order draft">
            This order could not be created in the ERP (Famous). It has not been
            approved — fix the cause below, then re-approve to try again.
          </T>
        </span>

        {report ? (
          <>
            <span className="font-medium">{report.headline}</span>
            {report.groups.map((group, groupIndex) => (
              <div
                key={`${group.reason}-${groupIndex}`}
                className="flex flex-col gap-1"
              >
                <span>{group.summary}</span>
                {group.remediation && (
                  <span className="font-medium">→ {group.remediation}</span>
                )}
                {group.items.length > 0 && (
                  <ul className="ml-4 list-disc">
                    {group.items.map((item, itemIndex) => (
                      <li key={`${item.label}-${itemIndex}`}>
                        {item.label}
                        {item.value && (
                          <span className="font-mono text-xs opacity-80">
                            {' '}
                            ({item.value})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {/*
              `report.raw` is the ERP's own text. `meta.message` is NOT a
              fallback here — once a report exists it holds the formatted report,
              so using it would make this toggle repeat what's already above.
            */}
            {report.raw && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowRaw((prev) => !prev)}
                  className="flex items-center gap-1 text-xs underline"
                >
                  <CaretDownIcon
                    className={`h-3 w-3 transition-transform ${showRaw ? 'rotate-180' : ''}`}
                  />
                  <T context="toggle raw ERP error text on sales order draft">
                    Show raw ERP response
                  </T>
                </button>
                {/*
                  `whitespace-pre-wrap`: `report.raw` is newline-separated (one
                  Famous annotation per line) and `sanitizeErpRawText` keeps
                  those breaks — without this the browser collapses them back
                  into the single unreadable block this all exists to avoid.
                */}
                {showRaw && (
                  <span className="mt-1 block font-mono text-xs break-words whitespace-pre-wrap opacity-80">
                    {report.raw}
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          message && (
            <span className="font-mono text-xs break-words opacity-80">
              {message}
            </span>
          )
        )}
      </AlertDescription>
    </AlertContainer>
  );
}
